// University of Maryland, College Park: public college page (#2840),
// official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UMD's Common Data Set 2025-2026, posted by the Office of Institutional
//    Research, Planning & Assessment as an Excel file
//    (CommonDataSet/CDS_2025-2026.xlsx, picked from the page's year menu):
//    C1 applicants, admits and enrollees for Fall 2025 with the residency
//    table, C8 test policy for Fall 2027, C21/C22 plans. The residency
//    table leaves the International column blank; in-state plus
//    out-of-state equals the total. G0 is checked "not available" for
//    2026-2027 costs, so the cost table uses the financial aid office.
//  - UMD Undergraduate Admissions: Application Deadlines, Freshman
//    Applicants, Freshman Application FAQs and Limited Enrollment Programs.
//    The deadlines table carries no year, and its footnote example still
//    names Fall 2024; the CDS gives the same early action dates for fall
//    2027 (closing 2026-11-01, notification by 2/1), so the table is read as
//    this cycle's.
//  - UMD Office of Student Financial Aid, Cost of Attendance, 2026-2027
//    estimated costs, undergraduate, on campus (Maryland resident and
//    non-Maryland resident). Business, engineering and computer science
//    majors pay additional differential tuition, not included.
//  - College Scorecard, school id 163286 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UMD Common Data Set 2025-2026',
        url: 'https://irpa.umd.edu/InstitutionalData/cds.html',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'UMD Undergraduate Admissions, Application Deadlines',
        url: 'https://admissions.umd.edu/apply/application-deadlines',
        read: '2026-10-01',
    },
    freshman: {
        label: 'UMD Undergraduate Admissions, Freshman Applicants',
        url: 'https://admissions.umd.edu/apply/freshman-applicants',
        read: '2026-10-01',
    },
    faqs: {
        label: 'UMD Undergraduate Admissions, Freshman Application FAQs',
        url: 'https://admissions.umd.edu/apply/freshman-application-faqs',
        read: '2026-10-01',
    },
    lep: {
        label: 'UMD Undergraduate Admissions, Limited Enrollment Programs',
        url: 'https://admissions.umd.edu/academics/limited-enrollment-programs',
        read: '2026-10-01',
    },
    coa: {
        label: 'UMD Office of Student Financial Aid, Cost of Attendance 2026-2027',
        url: 'https://financialaid.umd.edu/resources-policies/cost-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?163286-University-of-Maryland-College-Park',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-maryland-college-park',
    kbId: 'university_of_maryland_college_park',
    name: 'University of Maryland, College Park',
    shortName: 'UMD',
    aliases: ['UMD'],
    location: 'College Park, MD',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Maryland Athletics brand guidelines, red and gold); decoration only.
    branding: {
        primary: '#CE1126',
        secondary: '#FCD116',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_maryland_college_park_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 67101,
        admitted: 30218,
        enrolled: 6559,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 18487, admitted: 8075 },
            { group: 'Out-of-state', applied: 48614, admitted: 22143 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table (no separate international count)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UMD\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college, major or Limited Enrollment Program.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'UMD has no early decision plan. Its early action is non-binding and not restrictive, and the Common Data Set does not report early action counts, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You name a preferred major on the application. It does not affect whether you are admissible to UMD, but some majors add a review:',
        rows: [
            { school: 'Limited Enrollment Programs', requirement: 'Biochemistry, Biological Sciences, Business, Chemistry, Computer Science, Criminology and Criminal Justice, Engineering, Immersive Media Design, Neuroscience and Psychology have limited space and more competitive criteria. If you choose one, the program also reviews you. Most applicants are admitted to their preferred major; those who are not are placed in Letters & Sciences to work toward the gateway requirements.', source: 'lep' },
            { school: 'School of Music', requirement: 'An online application supplement and an audition, used only to decide admission to the School of Music, not to UMD.', source: 'freshman' },
            { school: 'Scholarships and special programs', requirement: 'Applying by the November 1 early action deadline gives you priority consideration for admission, merit-based scholarships and invitations to special programs.', source: 'freshman' },
        ],
        source: 'lep',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular deadline', value: 'January 20' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'By February 1', binding: false },
            { plan: 'Regular', deadline: 'January 20', decision: 'By April 1', binding: false },
        ],
        note: 'UMD strongly encourages applying by the early action deadline for priority consideration. Applications are complete only when every required document is in, submitted electronically or postmarked by the deadline. Applications received after January 20 are considered on a rolling, space-available basis. Fall applicants are automatically considered for spring admission. Admitted students confirm by May 1. The deadlines table carries no year.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'UMD is test-optional through the spring and fall 2027 application cycles. You say on the application whether you want your SAT or ACT scores considered, and that choice is final once you submit. If you send scores, UMD superscores both tests. Limited Enrollment Programs, merit scholarships and special programs are test-optional too. UMD says it is assessing the policy to set its requirement for cycles beyond fall 2027.',
        source: 'faqs',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for admission, but considered if submitted, for fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'faqs',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [11014, 42266] },
            { item: 'Mandatory fees', amounts: [1820, 1820] },
            { item: 'Housing (on campus, with cable and phone)', amounts: [10290, 10290] },
            { item: 'Food (on-campus plan, average)', amounts: [7180, 7180] },
            { item: 'Books and supplies', amounts: [1250, 1250] },
            { item: 'Transportation', amounts: [1514, 1514] },
            { item: 'Personal expenses', amounts: [1200, 1200] },
        ],
        totals: [34268, 65520],
        note: 'For a full-time undergraduate living on campus, fall and spring. Business, engineering and computer science majors pay additional tuition by semester hour, not shown here.',
        source: 'coa',
        netPrice: {
            average: 14296,
            byIncome: [
                { income: '$0 to $30,000', amount: 1141 },
                { income: '$30,001 to $48,000', amount: 5045 },
                { income: '$48,001 to $75,000', amount: 8768 },
                { income: '$75,001 to $110,000', amount: 16888 },
                { income: 'Over $110,000', amount: 27461 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UMD\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
